import { apiFetch, coreFetch, HttpMethod, MIME_TYPE, ResponseWithParsedData } from '@libs/core';
import urlJoin from 'url-join';
import type { ApiProxyClient, ApiProxyClientOptions, ApiProxyClientRequestBuilder } from '../types';

export function createApiProxyClient(
    basePath: string,
    requestBuilder: ApiProxyClientRequestBuilder = options => options
): ApiProxyClient {
    async function handleRequest<ReturnValue, TBody = BodyInit>(
        method: HttpMethod,
        pathname: string,
        options: ApiProxyClientOptions<TBody>
    ): Promise<Error | ReturnValue | ResponseWithParsedData<ReturnValue>>;
    async function handleRequest<ReturnValue, TBody = BodyInit>(
        method: HttpMethod,
        pathname: string,
        options?: ApiProxyClientOptions<TBody> & { useOldFetch: true }
    ): Promise<ReturnValue | null>;
    async function handleRequest<ReturnValue, TBody = BodyInit>(
        method: HttpMethod,
        pathname: string,
        options?: ApiProxyClientOptions<TBody>
    ): Promise<Error | ReturnValue | ResponseWithParsedData<ReturnValue> | null> {
        // @ts-expect-error -- I don't know how to fix this :)
        const { body, useOldFetch, ...builtOptions } = requestBuilder({
            method,
            ...options,
        });

        const defaultContentTypeHeader: HeadersInit =
            body instanceof FormData ? {} : { 'Content-Type': MIME_TYPE.json };
        const parsedBody = body instanceof FormData ? body : JSON.stringify(body);

        const combinedOptions = {
            cache: 'no-store' as const,
            ...builtOptions,
            body: parsedBody,
            headers: {
                ...defaultContentTypeHeader,
                ...builtOptions.headers,
            },
            method,
        };

        const { zodSchema, numberOfRetries, timeoutDuration, ...init } = combinedOptions;

        const inputString = urlJoin(basePath, pathname);

        if (useOldFetch) {
            return await coreFetch<ReturnValue>(inputString, init, zodSchema, numberOfRetries, timeoutDuration);
        } else {
            return await apiFetch<ReturnValue>(inputString, init, zodSchema, numberOfRetries, timeoutDuration);
        }
    }

    const createRequestHandler =
        (method: HttpMethod) =>
        async <ReturnValue, TBody>(
            pathname: string,
            options: ApiProxyClientOptions<TBody> & { useOldFetch?: boolean }
        ) =>
            handleRequest<ReturnValue, TBody>(method, pathname, options);

    return {
        get: createRequestHandler(HttpMethod.GET),
        post: createRequestHandler(HttpMethod.POST),
        put: createRequestHandler(HttpMethod.PUT),
        patch: createRequestHandler(HttpMethod.PATCH),
        options: createRequestHandler(HttpMethod.OPTIONS),
        delete: createRequestHandler(HttpMethod.DELETE),
        request: handleRequest,
    };
}
